import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useVoiceChat } from "@/hooks/useVoiceChat";
import { Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { 
  MessageSquare, 
  Send, 
  Loader2, 
  User,
  Bot,
  RefreshCw,
  StickyNote,
  Target,
  Utensils,
  Briefcase,
  Dumbbell,
  GraduationCap,
  Wallet,
  Save,
  Trash2,
  Plus,
  History,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Database,
  CalendarX,
} from "lucide-react";
import type { AiCoachConversation, AiCoachMessage } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";

interface ToolCall {
  tool: string;
  /** Whether the tool call succeeded. May be absent on older messages or partial responses; treat missing as success. */
  ok?: boolean;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolCallsMade?: ToolCall[];
  calendarNotConnected?: boolean;
  /** Marks a locally-generated failure bubble; never sent to the AI or saved. */
  isError?: boolean;
  /** The user text whose send failed, so Retry can re-post it. */
  failedMessage?: string;
  /** id of the user bubble for the failed turn — excluded from history on retry. */
  failedUserMessageId?: string;
}

interface SendVariables {
  text: string;
  /** id of the user bubble for this turn — excluded from conversationHistory. */
  userMessageId: string;
}

const TOOL_LABEL_MAP: Record<string, string> = {
  get_goals: "Goals",
  get_habits: "Habits",
  get_tasks: "Tasks",
  get_calendar: "Calendar",
  get_notes: "Notes",
  get_milestones: "Milestones",
  get_journal: "Journal",
  get_user_profile: "Profile",
};

function toolLabel(tool: string): string {
  return TOOL_LABEL_MAP[tool] ?? tool.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function ToolCallsChip({ toolCalls }: { toolCalls: ToolCall[] }) {
  const [expanded, setExpanded] = useState(false);
  // Missing `ok` (older messages / partial responses) means the fetch worked; only exclude explicit failures.
  const successful = toolCalls.filter(t => t.ok !== false);
  if (successful.length === 0) return null;
  const labels = successful.map(t => toolLabel(t.tool));
  return (
    <button
      onClick={() => setExpanded(v => !v)}
      className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors group"
      data-testid="tool-calls-chip"
    >
      <Database className="w-3 h-3 shrink-0" />
      {expanded ? (
        <span>Checked: {labels.join(", ")}</span>
      ) : (
        <span>Checked {successful.length} data source{successful.length !== 1 ? "s" : ""}</span>
      )}
      <ChevronDown
        className={`w-3 h-3 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
      />
    </button>
  );
}

interface SuggestedGoal {
  title: string;
  description: string;
  timeline?: string;
  milestones?: Array<{ title: string; description?: string }>;
  tasks?: Array<{ title: string; description?: string }>;
}

const initialMessage: Message = {
  id: "welcome",
  role: "assistant",
  content: `Welcome to Ask AI! I'm your personal planning assistant.

I can help you create detailed, actionable plans for:
- **Diet & Nutrition Plans** - Personalized meal plans and dietary guidance
- **Business Plans** - Strategy, goals, and roadmaps for your ventures
- **Workout Routines** - Fitness programs tailored to your goals
- **Learning Plans** - Study schedules and skill development paths
- **Financial Plans** - Budgeting and savings strategies

Tell me what you'd like to work on, and I'll ask you some questions to create a comprehensive plan. Once we're done, you can save it as a note or convert it into a goal with milestones!

What would you like to plan today?`,
  timestamp: new Date()
};

export default function AICoachAskPage() {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [inputValue, setInputValue] = useState("");
  const speakNextRef = useRef(false);

  const voice = useVoiceChat({
    onTranscript: (text) => {
      setInputValue("");
      sendChatText(text, true);
    },
    onInterim: (text) => setInputValue(text),
    onMicError: (message) => {
      toast({ title: "Microphone problem", description: message, variant: "destructive" });
    },
  });
  const [isComplete, setIsComplete] = useState(false);
  const [planContent, setPlanContent] = useState<string | null>(null);
  const [suggestedGoal, setSuggestedGoal] = useState<SuggestedGoal | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const { data: savedConversations = [], isLoading: loadingConversations } = useQuery<AiCoachConversation[]>({
    queryKey: ["/api/ai-coach/conversations"],
  });

  const { data: selectedConversation, isLoading: loadingSelected } = useQuery<{ conversation: AiCoachConversation; messages: AiCoachMessage[] }>({
    queryKey: ["/api/ai-coach/conversations", selectedConversationId],
    queryFn: async () => {
      const response = await fetch(`/api/ai-coach/conversations/${selectedConversationId}`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to load conversation");
      return response.json();
    },
    enabled: !!selectedConversationId,
  });

  useEffect(() => {
    if (selectedConversation && isViewingHistory) {
      const loadedMessages: Message[] = selectedConversation.messages.map(m => ({
        id: m.id,
        role: m.role as "user" | "assistant",
        content: m.content,
        timestamp: new Date(m.createdAt),
        toolCallsMade: Array.isArray(m.toolCallsMade) ? m.toolCallsMade : undefined
      }));
      setMessages(loadedMessages.length > 0 ? loadedMessages : [initialMessage]);
    }
  }, [selectedConversation, isViewingHistory]);

  const sendMessageMutation = useMutation({
    mutationFn: async ({ text, userMessageId }: SendVariables) => {
      // Exclude error bubbles and the current turn's own user bubble: the
      // message being sent must not also appear in the history (it would be
      // duplicated on retry, where the user bubble already exists).
      const history = messages
        .filter(m => !m.isError && m.id !== userMessageId)
        .map(m => ({ role: m.role, content: m.content }));
      const response = await apiRequest("POST", "/api/ai-coach/ask", {
        message: text,
        conversationHistory: history
      });
      return response.json();
    },
    onSuccess: (data) => {
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date(),
        toolCallsMade: Array.isArray(data.toolCallsMade) ? data.toolCallsMade : undefined,
        calendarNotConnected: data.calendarNotConnected === true,
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (speakNextRef.current || voice.voiceReplies) {
        voice.speakText(data.response);
      }
      speakNextRef.current = false;
      
      if (data.isComplete) {
        setIsComplete(true);
        if (data.planContent) {
          setPlanContent(data.planContent);
        }
        if (data.suggestedGoal) {
          setSuggestedGoal(data.suggestedGoal);
        }
      }
    },
    onError: (_error: Error, { text, userMessageId }: SendVariables) => {
      speakNextRef.current = false;
      // Don't keep the hands-free mic loop running into an error state.
      voice.stopListening();
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "Sorry, I couldn't respond just now. Please check your connection and try again.",
        timestamp: new Date(),
        isError: true,
        failedMessage: text,
        failedUserMessageId: userMessageId,
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  });

  const handleRetry = (errorMessage: Message) => {
    if (sendMessageMutation.isPending || !errorMessage.failedMessage) return;
    setMessages(prev => prev.filter(m => m.id !== errorMessage.id));
    sendMessageMutation.mutate({
      text: errorMessage.failedMessage,
      userMessageId: errorMessage.failedUserMessageId ?? "",
    });
  };

  const saveConversationMutation = useMutation({
    mutationFn: async (title: string) => {
      const messagesToSave = messages.filter(m => m.id !== "welcome" && !m.isError);
      const response = await apiRequest("POST", "/api/ai-coach/conversations", {
        title,
        messages: messagesToSave.map(m => ({ role: m.role, content: m.content, toolCallsMade: m.toolCallsMade }))
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Conversation Saved",
        description: "Your conversation has been saved to history."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/ai-coach/conversations"] });
      setShowSaveDialog(false);
      setSaveTitle("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save conversation",
        variant: "destructive"
      });
    }
  });

  const deleteConversationMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/ai-coach/conversations/${id}`, {});
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Conversation Deleted",
        description: "The conversation has been removed from history."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/ai-coach/conversations"] });
      if (selectedConversationId) {
        setSelectedConversationId(null);
        setIsViewingHistory(false);
        resetConversation();
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete conversation",
        variant: "destructive"
      });
    }
  });

  const sendChatText = (raw: string, fromVoice = false) => {
    const text = raw.trim();
    if (!text || sendMessageMutation.isPending) return;

    speakNextRef.current = fromVoice;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    sendMessageMutation.mutate({ text, userMessageId: userMessage.id });
    setInputValue("");
    
    if (isViewingHistory) {
      setIsViewingHistory(false);
      setSelectedConversationId(null);
    }
  };

  const handleSendMessage = () => sendChatText(inputValue);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (action: string) => {
    setInputValue(action);
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const resetConversation = () => {
    setMessages([initialMessage]);
    setInputValue("");
    setIsComplete(false);
    setPlanContent(null);
    setSuggestedGoal(null);
    setSelectedConversationId(null);
    setIsViewingHistory(false);
  };

  const handleSaveConversation = () => {
    const userMessages = messages.filter(m => m.id !== "welcome" && !m.isError);
    if (userMessages.length < 2) {
      toast({
        title: "Nothing to save",
        description: "Have a conversation first before saving.",
        variant: "destructive"
      });
      return;
    }
    const defaultTitle = suggestedGoal?.title || `Chat ${format(new Date(), 'MMM d, yyyy h:mm a')}`;
    setSaveTitle(defaultTitle);
    setShowSaveDialog(true);
  };

  const handleLoadConversation = (id: string) => {
    setSelectedConversationId(id);
    setIsViewingHistory(true);
    setIsComplete(false);
    setPlanContent(null);
    setSuggestedGoal(null);
  };

  const createNoteMutation = useMutation({
    mutationFn: async () => {
      const content = planContent || messages
        .filter(m => m.role === "assistant" && m.id !== "welcome" && !m.isError)
        .map(m => m.content)
        .join("\n\n---\n\n");
      
      const response = await apiRequest("POST", "/api/notes", {
        title: suggestedGoal?.title || "AI Generated Plan",
        content: { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: content }] }] },
        plainContent: content,
        tags: ["ai-generated", "plan"],
        type: "note"
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Note Created!",
        description: "Your plan has been saved as a note."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      resetConversation();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create note",
        variant: "destructive"
      });
    }
  });

  // Persists the created goal so a network-error retry can skip re-posting it
  // and resume from the task loop with the original goal ID and title.
  const createGoalRef = useRef<{ id: string; title: string } | null>(null);
  // Idempotency keys for each task in the goal creation loop — keyed by index
  // so a network-error retry reuses the same key per slot. Cleared on success.
  const createGoalTaskIdempotencyKeysRef = useRef<string[]>([]);
  // A new or cleared suggested goal means a different submission — clear any
  // prior attempt so retries of the new goal cannot reuse stale keys.
  useEffect(() => {
    createGoalRef.current = null;
    createGoalTaskIdempotencyKeysRef.current = [];
  }, [suggestedGoal]);

  const createGoalMutation = useMutation({
    mutationFn: async () => {
      const conversationContent = messages
        .filter(m => m.role === "assistant" && m.id !== "welcome" && !m.isError)
        .map(m => m.content)
        .join("\n\n");
      
      const firstUserMessage = messages.find(m => m.role === "user");
      const goalTitle = suggestedGoal?.title || firstUserMessage?.content.slice(0, 100) || "AI Generated Goal";
      const goalDescription = suggestedGoal?.description || conversationContent.slice(0, 500) || "";

      // Create goal only if a prior attempt has not already succeeded.
      let goal: { id: string; title: string };
      if (createGoalRef.current) {
        goal = createGoalRef.current;
      } else {
        const response = await apiRequest("POST", "/api/goals", {
          title: goalTitle,
          description: goalDescription,
          progress: 0,
          category: ""
        });
        goal = await response.json();
        createGoalRef.current = { id: goal.id, title: goal.title };
      }

      if (suggestedGoal?.milestones && suggestedGoal.milestones.length > 0) {
        for (const milestone of suggestedGoal.milestones) {
          await apiRequest("POST", "/api/milestones", {
            goalId: goal.id,
            title: milestone.title,
            description: milestone.description || "",
            completed: false
          });
        }
      }

      if (suggestedGoal?.tasks && suggestedGoal.tasks.length > 0) {
        for (let i = 0; i < suggestedGoal.tasks.length; i++) {
          const task = suggestedGoal.tasks[i];
          if (!createGoalTaskIdempotencyKeysRef.current[i]) {
            createGoalTaskIdempotencyKeysRef.current[i] = crypto.randomUUID();
          }
          await apiRequest("POST", "/api/tasks", {
            title: task.title,
            description: task.description || "",
            goalId: goal.id,
            completed: false,
            priority: "medium"
          }, {
            "Idempotency-Key": createGoalTaskIdempotencyKeysRef.current[i],
          });
        }
      }

      return goal;
    },
    onSuccess: (goal) => {
      createGoalRef.current = null;
      createGoalTaskIdempotencyKeysRef.current = [];
      toast({
        title: "Goal Created!",
        description: `Your goal "${goal.title}" has been created with milestones and tasks.`
      });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      resetConversation();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create goal",
        variant: "destructive"
      });
    }
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const quickPrompts = [
    { label: "Diet Plan", icon: Utensils, action: "I want to create a personalized diet plan" },
    { label: "Business Plan", icon: Briefcase, action: "Help me develop a business plan" },
    { label: "Workout Routine", icon: Dumbbell, action: "I need a workout routine" },
    { label: "Learning Plan", icon: GraduationCap, action: "Create a learning plan for me" },
    { label: "Budget Plan", icon: Wallet, action: "Help me create a budget and savings plan" }
  ];

  const hasUnsavedMessages = messages.filter(m => m.id !== "welcome" && !m.isError).length >= 2;

  return (
    <div className="flex h-[calc(100vh-4rem)] gap-4 p-4">
      {/* Left Sidebar - Saved Conversations */}
      <div className={`${sidebarCollapsed ? 'w-12' : 'w-72'} shrink-0 transition-all duration-300`}>
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              {!sidebarCollapsed && (
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Saved Chats
                </CardTitle>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                data-testid="button-toggle-sidebar"
              >
                {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {!sidebarCollapsed && (
            <CardContent className="flex-1 overflow-hidden p-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full mb-3"
                onClick={resetConversation}
                data-testid="button-new-conversation"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Chat
              </Button>
              <ScrollArea className="h-[calc(100%-3rem)]">
                {loadingConversations ? (
                  <div className="flex justify-center p-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : savedConversations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center p-4">
                    No saved conversations yet
                  </p>
                ) : (
                  <div className="space-y-1">
                    {savedConversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`group p-2 rounded-lg cursor-pointer hover:bg-muted/80 transition-colors ${
                          selectedConversationId === conv.id ? 'bg-muted' : ''
                        }`}
                        onClick={() => handleLoadConversation(conv.id)}
                        data-testid={`conversation-item-${conv.id}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{conv.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(conv.lastMessageAt), 'MMM d, yyyy')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('Delete this conversation?')) {
                                deleteConversationMutation.mutate(conv.id);
                              }
                            }}
                            data-testid={`button-delete-conversation-${conv.id}`}
                          >
                            <Trash2 className="w-3 h-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Main Chat Area */}
      <Card className="flex-1 flex flex-col">
        <CardHeader className="pb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl flex items-center gap-2" data-testid="title-ask-ai">
                  <MessageSquare className="w-5 h-5" />
                  Ask AI
                  {isViewingHistory && selectedConversation && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      — {selectedConversation.conversation.title}
                    </span>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Your personal planning assistant for creating actionable plans
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {hasUnsavedMessages && !isViewingHistory && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveConversation}
                    disabled={saveConversationMutation.isPending}
                    data-testid="button-save-conversation"
                  >
                    {saveConversationMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Chat
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => createNoteMutation.mutate()}
                    disabled={createNoteMutation.isPending}
                    data-testid="button-add-as-notepad-header"
                  >
                    {createNoteMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <StickyNote className="w-4 h-4 mr-2" />
                    )}
                    Add as Notepad
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => createGoalMutation.mutate()}
                    disabled={createGoalMutation.isPending}
                    data-testid="button-create-as-goal-header"
                  >
                    {createGoalMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Target className="w-4 h-4 mr-2" />
                    )}
                    Create as Goal
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={resetConversation}
                data-testid="button-reset-conversation"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col flex-1 overflow-hidden">
          {messages.length === 1 && !isViewingHistory && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {quickPrompts.map((prompt) => (
                <Button
                  key={prompt.label}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction(prompt.action)}
                  data-testid={`button-quick-${prompt.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <prompt.icon className="w-4 h-4 mr-2" />
                  {prompt.label}
                </Button>
              ))}
            </div>
          )}

          <ScrollArea 
            className="flex-1 pr-4 mb-4"
            data-testid="chat-messages"
          >
            {loadingSelected ? (
              <div className="flex justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {message.role === "assistant" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          <Bot className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex flex-col">
                      <div
                        className={`max-w-[80%] rounded-lg p-4 ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : message.isError
                              ? "bg-destructive/10 border border-destructive/30 text-destructive"
                              : "bg-muted"
                        }`}
                        data-testid={`message-${message.role}-${message.id}`}
                      >
                        <div className="whitespace-pre-wrap text-sm prose prose-sm dark:prose-invert max-w-none">
                          {message.content.split('\n').map((line, i) => {
                            if (line.startsWith('**') && line.endsWith('**')) {
                              return <p key={i} className="font-semibold my-1">{line.replace(/\*\*/g, '')}</p>;
                            }
                            if (line.match(/^\*\*.*\*\*/)) {
                              const cleaned = line.replace(/\*\*/g, '');
                              return <p key={i} className="font-medium my-1">{cleaned}</p>;
                            }
                            if (line.startsWith('- ')) {
                              return <p key={i} className="my-0.5 ml-2">{line}</p>;
                            }
                            return <p key={i} className="my-1">{line}</p>;
                          })}
                        </div>
                      </div>
                      {message.role === "assistant" && message.isError && message.failedMessage != null && (
                        <button
                          onClick={() => handleRetry(message)}
                          disabled={sendMessageMutation.isPending}
                          className="mt-1.5 flex items-center gap-1.5 self-start text-xs font-medium text-primary hover:opacity-80 disabled:opacity-50"
                          data-testid="button-retry-send"
                          aria-label="Retry sending message"
                        >
                          <RefreshCw className="w-3 h-3 shrink-0" />
                          Retry
                        </button>
                      )}
                      {message.role === "assistant" && message.toolCallsMade && message.toolCallsMade.length > 0 && (
                        <ToolCallsChip toolCalls={message.toolCallsMade} />
                      )}
                      {message.role === "assistant" && message.calendarNotConnected && isAdmin && (
                        <div
                          className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                          data-testid="calendar-not-connected-notice"
                        >
                          <CalendarX className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>
                            Google Calendar is not connected.{" "}
                            <a
                              href="/settings"
                              className="underline font-medium hover:opacity-80"
                            >
                              Go to Settings → Integrations
                            </a>{" "}
                            to authorize it.
                          </span>
                        </div>
                      )}
                    </div>
                    {message.role === "user" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-secondary">
                          <User className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
                {sendMessageMutation.isPending && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="w-8 h-8 shrink-0">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <Bot className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg p-4 flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Thinking…</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground/70 text-xs">
                        <Database className="w-3 h-3" />
                        <span>Fetching your data</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {isComplete && (
            <div className="mb-4 p-4 border rounded-lg bg-muted/50 flex-shrink-0">
              <p className="text-sm font-medium mb-3">
                Your plan is ready! What would you like to do with it?
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={handleSaveConversation}
                  disabled={saveConversationMutation.isPending}
                  variant="outline"
                  data-testid="button-save-chat"
                >
                  {saveConversationMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Chat
                </Button>
                <Button
                  onClick={() => createNoteMutation.mutate()}
                  disabled={createNoteMutation.isPending || createGoalMutation.isPending}
                  variant="outline"
                  data-testid="button-add-as-notepad"
                >
                  {createNoteMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <StickyNote className="w-4 h-4 mr-2" />
                  )}
                  Add as Notepad
                </Button>
                <Button
                  onClick={() => createGoalMutation.mutate()}
                  disabled={createGoalMutation.isPending || createNoteMutation.isPending}
                  data-testid="button-create-as-goal"
                >
                  {createGoalMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Target className="w-4 h-4 mr-2" />
                  )}
                  Create as Goal
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 items-end flex-shrink-0">
            <Textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={voice.listening ? "Listening…" : "Type your message... (Shift+Enter for new line)"}
              disabled={sendMessageMutation.isPending}
              className="flex-1 min-h-[44px] max-h-[200px] resize-none"
              rows={1}
              data-testid="input-chat-message"
            />
            {voice.ttsSupported && (
              <Button
                variant={voice.voiceReplies ? "secondary" : "outline"}
                size="icon"
                className="h-[44px] w-[44px]"
                title={voice.voiceReplies ? "Voice replies on — the coach reads answers aloud" : "Voice replies off"}
                onClick={() => {
                  if (voice.speaking) voice.stopSpeaking();
                  voice.setVoiceReplies(!voice.voiceReplies);
                }}
                data-testid="button-coach-voice-replies"
              >
                {voice.voiceReplies ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
            )}
            {voice.sttSupported && (
              <Button
                onClick={voice.toggleListening}
                size="icon"
                variant={voice.listening || voice.conversationMode ? "destructive" : "outline"}
                className="h-[44px] w-[44px]"
                title={
                  voice.listening || voice.conversationMode
                    ? "End voice conversation"
                    : "Start voice conversation"
                }
                data-testid="button-coach-mic"
              >
                {voice.listening || voice.conversationMode ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            )}
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || sendMessageMutation.isPending}
              className="h-[44px]"
              data-testid="button-send-message"
            >
              {sendMessageMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Conversation Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Conversation</DialogTitle>
            <DialogDescription>
              Give your conversation a title to save it for later reference.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              placeholder="Conversation title..."
              data-testid="input-save-title"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              data-testid="button-cancel-save"
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveConversationMutation.mutate(saveTitle)}
              disabled={!saveTitle.trim() || saveConversationMutation.isPending}
              data-testid="button-confirm-save"
            >
              {saveConversationMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
